import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import {
  cloneListOfCloneable, DataModel, DataModelBuilder, isSameData,
  isSameList, SerializationContext, SerializedData,
} from '../../dto/dto.js';
import { LegacyReceipt } from './legacy-receipt.js';
import { LegacyReceiptGroup } from './legacy-receipt-group.js';
import { ReceiptScope } from './receipt-scope.js';
import { OfflineReceiptGroup } from './offline-receipt-group.js';
import { OfflineReceipt } from './offline-receipt.js';

export class OfflineInvoicingDetails extends DataModel {
  static readonly TYPE_ID: string = '157318758785484a9a15e0d598f2f141';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    private readonly entries: OfflineReceiptGroup[],
  ) {
    super();
  }

  get $typeId() {
    return OfflineInvoicingDetails.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(OfflineInvoicingDetails.SCHEMA_VERSION);
  }

  private static _create({ entries }: { entries: OfflineReceiptGroup[] }): OfflineInvoicingDetails {
    return new OfflineInvoicingDetails(entries);
  }

  static empty(): OfflineInvoicingDetails {
    return this._create({ entries: [] });
  }

  static fromLegacy(currentReceipt: LegacyReceipt | null, pastReceipts: LegacyReceipt[]) {
    return this._create({
      entries: [
        OfflineReceiptGroup.fromLegacyReceipts(currentReceipt, pastReceipts),
      ],
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): OfflineInvoicingDetails {
    return new OfflineInvoicingDetails(
        MapUtils.getList(map, 'entries', (data) => {
          return context.fromData<OfflineReceiptGroup>(data as SerializedData);
        }),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'entries': this.entries.map((e) => context.toData(e)),
    };
  }

  clone(): this {
    return new OfflineInvoicingDetails(
        cloneListOfCloneable(this.entries),
    ) as this;
  }

  isSame(other: OfflineInvoicingDetails): boolean {
    if(this === other) {
      return true;
    }

    return isSameList(other.entries, this.entries, isSameData);
  }

  getTotalNumberOfCreatedReceiptForGroupId(groupId: string): number | null {
    return this.getReceiptGroupById(groupId)?.getTotalNumberOfCreatedReceipts() ?? null;
  }

  getTotalNumberOfCreatedReceiptForScope(scope: ReceiptScope): number | null {
    return this.getReceiptGroupByScope(scope)?.getTotalNumberOfCreatedReceipts() ?? null;
  }

  hasActiveReceiptForGroup(receiptGroupId: string): boolean {
    for(let i = 0; i < this.entries.length; i++) {
      if(this.entries[i].uniqueId === receiptGroupId) {
        return this.entries[i].hasActiveReceipt();
      }
    }

    return false;
  }

  hasActiveReceiptForScope(scope: ReceiptScope): boolean {
    for(let i = 0; i < this.entries.length; i++) {
      if(this.entries[i].scope === scope) {
        return this.entries[i].hasActiveReceipt();
      }
    }

    return false;
  }

  addEntry(entry: OfflineReceiptGroup): void {
    this.entries.push(entry);
  }

  getReceiptGroupById(receiptGroupId: string): OfflineReceiptGroup | null {
    for(let i = 0; i < this.entries.length; i++) {
      if(this.entries[i].uniqueId === receiptGroupId) {
        return this.entries[i];
      }
    }

    return null;
  }

  getOrAddReceiptGroupById(receiptGroupId: string, options: { orAdd: () => OfflineReceiptGroup }): OfflineReceiptGroup {
    for(let i = 0; i < this.entries.length; i++) {
      if(this.entries[i].uniqueId === receiptGroupId) {
        return this.entries[i];
      }
    }

    this.entries.push(options.orAdd());
    return this.entries[this.entries.length - 1];
  }

  getReceiptGroupByScope(scope: ReceiptScope): OfflineReceiptGroup | null {
    for(let i = 0; i < this.entries.length; i++) {
      if(this.entries[i].scope === scope) {
        return this.entries[i];
      }
    }

    return null;
  }

  getOrAddReceiptGroupByScope(scope: ReceiptScope, options: { orAdd: () => OfflineReceiptGroup }): OfflineReceiptGroup {
    for(let i = 0; i < this.entries.length; i++) {
      if(this.entries[i].scope === scope) {
        return this.entries[i];
      }
    }

    this.entries.push(options.orAdd());
    return this.entries[this.entries.length - 1];
  }

  getReceiptsForCheckout(): OfflineReceipt[] {
    const receipts: OfflineReceipt[] = [];
    for(const entry of this.entries) {
      receipts.push(...entry.getReceiptsForCheckout());
    }

    return receipts;
  }

  getReceiptsForExport(): OfflineReceipt[] {
    const receipts: OfflineReceipt[] = [];
    for(const entry of this.entries) {
      receipts.push(...entry.getReceiptsForExport());
    }

    return receipts;
  }

  toDiagnostics(): SerializedData {
    return {
      'entries': this.entries.map((e) => e.toDiagnostics()),
    };
  }
}

export class OfflineInvoicingDetailsBuilder extends DataModelBuilder<OfflineInvoicingDetails> {
  build(context: SerializationContext, map: SerializedData): OfflineInvoicingDetails {
    return OfflineInvoicingDetails.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(OfflineInvoicingDetails.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OfflineInvoicingDetails.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      const oldEntries = MapUtils.getList(map, 'entries', (data) => {
        return context.fromData<LegacyReceiptGroup>(data as SerializedData);
      });

      const entries = oldEntries.map((e) => OfflineReceiptGroup.fromLegacyGroup(e));
      map['entries'] = entries.map((e) => context.toData(e));

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
